import { supabase } from '../lib/supabase';
import crypto from 'crypto-js';

const EDGE_FUNCTION_URL = import.meta.env?.VITE_SUPABASE_URL + '/functions/v1';

class TwoFactorAuthService {
  // Get 2FA settings for current user
  async get2FASettings() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('two_factor_auth')?.select('*')?.eq('user_id', user?.id)?.single();

    if (error && error?.code !== 'PGRST116') throw error;
    
    return data || {
      enabled: false,
      enforced: false,
      sms_enabled: false,
      email_enabled: false,
      authenticator_enabled: false,
      default_method: null
    };
  }

  // Generate TOTP secret for authenticator app
  generateTOTPSecret() {
    let secret = this.generateRandomSecret(32);
    return secret;
  }

  // Generate random secret
  generateRandomSecret(length = 32) {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567'; // Base32 alphabet
    let secret = '';
    for (let i = 0; i < length; i++) {
      secret += chars?.charAt(Math.floor(Math.random() * chars?.length));
    }
    return secret;
  }

  // Generate QR code URL for authenticator app
  generateQRCodeURL(secret, email) {
    const issuer = 'DraftIQ';
    const label = `${issuer}:${email}`;
    return `otpauth://totp/${encodeURIComponent(label)}?secret=${secret}&issuer=${encodeURIComponent(issuer)}`;
  }

  // Verify TOTP code
  verifyTOTPCode(secret, token) {
    // Simple TOTP verification (30-second window)
    const time = Math.floor(Date.now() / 1000 / 30);
    
    for (let i = -1; i <= 1; i++) {
      const testToken = this.generateTOTPToken(secret, time + i);
      if (testToken === token) {
        return true;
      }
    }
    return false;
  }

  // Generate TOTP token (simplified implementation)
  generateTOTPToken(secret, time) {
    const counter = time?.toString(16)?.padStart(16, '0');
    const hmac = crypto?.HmacSHA1(counter, secret);
    const hash = hmac?.toString();
    const offset = parseInt(hash?.slice(-1), 16);
    const binary = parseInt(hash?.substr(offset * 2, 8), 16) & 0x7fffffff;
    const token = (binary % 1000000)?.toString()?.padStart(6, '0');
    return token;
  }

  // Setup SMS 2FA
  async setupSMS(phoneNumber) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Generate verification code
    const verificationCode = Math.floor(100000 + Math.random() * 900000)?.toString();

    // Send verification code
    await this.sendVerificationCode('sms', phoneNumber, null, verificationCode);

    // Update 2FA settings (not verified yet)
    const { data, error } = await supabase?.from('two_factor_auth')?.upsert({
        user_id: user?.id,
        sms_phone_number: phoneNumber,
        sms_verified: false,
        updated_at: new Date()?.toISOString()
      }, { onConflict: 'user_id' })?.select()?.single();

    if (error) throw error;
    return { success: true, verificationCode };
  }

  // Setup Email 2FA
  async setupEmail(email) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Generate verification code
    const verificationCode = Math.floor(100000 + Math.random() * 900000)?.toString();

    // Send verification code
    await this.sendVerificationCode('email', null, email, verificationCode);

    // Update 2FA settings (not verified yet)
    const { data, error } = await supabase?.from('two_factor_auth')?.upsert({
        user_id: user?.id,
        email_address: email,
        email_verified: false,
        updated_at: new Date()?.toISOString()
      }, { onConflict: 'user_id' })?.select()?.single();

    if (error) throw error;
    return { success: true, verificationCode };
  }

  // Setup Authenticator App 2FA
  async setupAuthenticator() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    let secret = this.generateTOTPSecret();
    const qrCodeURL = this.generateQRCodeURL(secret, user?.email);

    // Store secret (not verified yet)
    const { data, error } = await supabase?.from('two_factor_auth')?.upsert({
        user_id: user?.id,
        authenticator_secret: secret,
        authenticator_verified: false,
        updated_at: new Date()?.toISOString()
      }, { onConflict: 'user_id' })?.select()?.single();

    if (error) throw error;
    return { secret, qrCodeURL };
  }

  // Verify 2FA setup
  async verifySetup(method, code) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const settings = await this.get2FASettings();

    let verified = false;

    if (method === 'sms') {
      verified = await this.verifyCode(code, 'sms');
      if (verified) {
        await supabase?.from('two_factor_auth')?.update({
            sms_verified: true,
            sms_verified_at: new Date()?.toISOString(),
            sms_enabled: true
          })?.eq('user_id', user?.id);
      }
    } else if (method === 'email') {
      verified = await this.verifyCode(code, 'email');
      if (verified) {
        await supabase?.from('two_factor_auth')?.update({
            email_verified: true,
            email_verified_at: new Date()?.toISOString(),
            email_enabled: true
          })?.eq('user_id', user?.id);
      }
    } else if (method === 'authenticator') {
      verified = this.verifyTOTPCode(settings?.authenticator_secret, code);
      if (verified) {
        await supabase?.from('two_factor_auth')?.update({
            authenticator_verified: true,
            authenticator_verified_at: new Date()?.toISOString(),
            authenticator_enabled: true
          })?.eq('user_id', user?.id);
      }
    }

    return verified;
  }

  // Verify code from database
  async verifyCode(code, method) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: attempts, error } = await supabase?.from('two_factor_attempts')?.select('*')?.eq('user_id', user?.id)?.eq('method', method)?.gte('expires_at', new Date()?.toISOString())?.order('created_at', { ascending: false })?.limit(1);

    if (error) throw error;
    if (!attempts || attempts?.length === 0) return false;

    return attempts?.[0]?.code_sent === code;
  }

  // Send verification code via edge function
  async sendVerificationCode(method, phoneNumber, email, code) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: { session } } = await supabase?.auth?.getSession();
    if (!session) throw new Error('Not authenticated');

    const response = await fetch(`${EDGE_FUNCTION_URL}/send-2fa-code`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token}`
      },
      body: JSON.stringify({
        userId: user?.id,
        method,
        phoneNumber,
        email,
        code
      })
    });

    if (!response?.ok) {
      const error = await response?.json();
      throw new Error(error.error || 'Failed to send verification code');
    }

    return await response?.json();
  }

  // Generate backup codes
  async generateBackupCodes() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Delete existing backup codes
    await supabase?.from('two_factor_backup_codes')?.delete()?.eq('user_id', user?.id);

    // Generate 10 backup codes
    const codes = [];
    for (let i = 0; i < 10; i++) {
      const code = Math.random()?.toString(36)?.substring(2, 10)?.toUpperCase();
      codes?.push(code);

      // Hash and store code
      const codeHash = crypto?.SHA256(code)?.toString();
      await supabase?.from('two_factor_backup_codes')?.insert({
          user_id: user?.id,
          code_hash: codeHash
        });
    }

    // Update 2FA settings
    await supabase?.from('two_factor_auth')?.update({
        backup_codes_generated: true,
        backup_codes_generated_at: new Date()?.toISOString()
      })?.eq('user_id', user?.id);

    return codes;
  }

  // Verify backup code
  async verifyBackupCode(code) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const codeHash = crypto?.SHA256(code?.toUpperCase())?.toString();

    const { data: backupCodes, error } = await supabase?.from('two_factor_backup_codes')?.select('*')?.eq('user_id', user?.id)?.eq('code_hash', codeHash)?.eq('used', false)?.gte('expires_at', new Date()?.toISOString());

    if (error) throw error;
    if (!backupCodes || backupCodes?.length === 0) return false;

    // Mark code as used
    await supabase?.from('two_factor_backup_codes')?.update({
        used: true,
        used_at: new Date()?.toISOString()
      })?.eq('id', backupCodes?.[0]?.id);

    return true;
  }

  // Enable 2FA
  async enable2FA(defaultMethod) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('two_factor_auth')?.update({
        enabled: true,
        default_method: defaultMethod,
        updated_at: new Date()?.toISOString()
      })?.eq('user_id', user?.id)?.select()?.single();

    if (error) throw error;
    return data;
  }

  // Disable 2FA
  async disable2FA() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('two_factor_auth')?.update({
        enabled: false,
        sms_enabled: false,
        email_enabled: false,
        authenticator_enabled: false,
        updated_at: new Date()?.toISOString()
      })?.eq('user_id', user?.id)?.select()?.single();

    if (error) throw error;
    return data;
  }

  // Check if device is trusted
  async isDeviceTrusted(deviceFingerprint) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) return false;

    const { data: devices, error } = await supabase?.from('trusted_devices')?.select('*')?.eq('user_id', user?.id)?.eq('device_fingerprint', deviceFingerprint)?.gte('expires_at', new Date()?.toISOString());

    if (error) return false;
    return devices && devices?.length > 0;
  }

  // Add trusted device
  async addTrustedDevice(deviceFingerprint, deviceName, deviceInfo, ipAddress, location) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const settings = await this.get2FASettings();
    const expiresAt = new Date(Date.now() + (settings.remember_device_days || 30) * 24 * 60 * 60 * 1000);

    const { data, error } = await supabase?.from('trusted_devices')?.upsert({
        user_id: user?.id,
        device_fingerprint: deviceFingerprint,
        device_name: deviceName,
        device_info: deviceInfo,
        ip_address: ipAddress,
        location,
        expires_at: expiresAt?.toISOString(),
        last_used_at: new Date()?.toISOString()
      }, { onConflict: 'user_id,device_fingerprint' })?.select()?.single();

    if (error) throw error;
    return data;
  }

  // Get trusted devices
  async getTrustedDevices() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('trusted_devices')?.select('*')?.eq('user_id', user?.id)?.gte('expires_at', new Date()?.toISOString())?.order('last_used_at', { ascending: false });

    if (error) throw error;
    return data;
  }

  // Remove trusted device
  async removeTrustedDevice(deviceId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase?.from('trusted_devices')?.delete()?.eq('id', deviceId)?.eq('user_id', user?.id);

    if (error) throw error;
    return { success: true };
  }

  // Request 2FA code for login
  async request2FACode(method) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const settings = await this.get2FASettings();
    const code = Math.floor(100000 + Math.random() * 900000)?.toString();

    if (method === 'sms' && settings?.sms_enabled) {
      await this.sendVerificationCode('sms', settings?.sms_phone_number, null, code);
    } else if (method === 'email' && settings?.email_enabled) {
      await this.sendVerificationCode('email', null, settings?.email_address || user?.email, code);
    } else {
      throw new Error('2FA method not enabled');
    }

    return { success: true };
  }
}

export default new TwoFactorAuthService();